import React, { useState } from "react";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_create_board_checklist,
  API_update_board_checklist,
  API_delete_board_checklist,
  API_create_board_checklist_item,
  API_update_board_checklist_item,
  API_delete_board_checklist_item,
} from "../../../constants";

// Mirrors the MUI input styling used across the app's modals.
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

const checkboxSx = {
  color: "#94a3b8",
  padding: "3px",
  "&.Mui-checked": { color: "#6d28d9" },
};

/* A single checklist: header, progress bar, items, and an add-item composer. */
function ChecklistBlock({
  checklist,
  onToggleItem,
  onAddItem,
  onDeleteItem,
  onRename,
  onDelete,
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(checklist.name);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [itemDraft, setItemDraft] = useState("");

  const items = checklist.items || [];
  const total = checklist.total_count != null ? checklist.total_count : items.length;
  const completed =
    checklist.completed_count != null
      ? checklist.completed_count
      : items.filter((i) => i.is_complete).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== checklist.name) {
      onRename(checklist.uuid, trimmed);
    } else {
      setNameDraft(checklist.name);
    }
    setIsRenaming(false);
  };

  const submitItem = () => {
    const trimmed = itemDraft.trim();
    if (!trimmed) return;
    onAddItem(checklist.uuid, trimmed);
    setItemDraft("");
  };

  return (
    <div className="mb-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        {isRenaming ? (
          <TextField
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
              } else if (e.key === "Escape") {
                setNameDraft(checklist.name);
                setIsRenaming(false);
              }
            }}
            variant="outlined"
            size="small"
            inputProps={{ maxLength: 255 }}
            sx={textFieldSx}
          />
        ) : (
          <h4
            className="text-sm font-semibold text-slate-700 cursor-text rounded px-1 hover:bg-slate-100"
            onClick={() => {
              setNameDraft(checklist.name);
              setIsRenaming(true);
            }}
          >
            {checklist.name}
          </h4>
        )}
        <span className="text-xs text-slate-400">
          {completed}/{total}
        </span>
        <button
          className="ml-auto text-slate-400 hover:text-red-600"
          onClick={() => onDelete(checklist.uuid)}
          title="Delete checklist"
        >
          <CloseIcon style={{ fontSize: 16 }} />
        </button>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-violet-600 rounded-full"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Items */}
      {items.map((item) => (
        <div key={item.uuid} className="group flex items-center gap-1.5 py-0.5">
          <Checkbox
            checked={!!item.is_complete}
            onChange={() => onToggleItem(checklist, item)}
            size="small"
            sx={checkboxSx}
          />
          <span
            className={`flex-1 text-sm whitespace-pre-wrap break-words ${
              item.is_complete ? "line-through text-slate-400" : "text-slate-700"
            }`}
          >
            {item.text}
          </span>
          <button
            className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
            onClick={() => onDeleteItem(item.uuid)}
            title="Delete item"
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        </div>
      ))}

      {/* Add item */}
      {isAddingItem ? (
        <div className="mt-1.5">
          <TextField
            autoFocus
            value={itemDraft}
            onChange={(e) => setItemDraft(e.target.value)}
            placeholder="Add an item…"
            fullWidth
            variant="outlined"
            size="small"
            inputProps={{ maxLength: 500 }}
            sx={textFieldSx}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitItem();
              } else if (e.key === "Escape") {
                setIsAddingItem(false);
                setItemDraft("");
              }
            }}
          />
          <div className="flex items-center gap-2 mt-1.5">
            <button
              className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
              onClick={submitItem}
            >
              Add
            </button>
            <button
              className="text-slate-500 hover:text-slate-700"
              onClick={() => {
                setIsAddingItem(false);
                setItemDraft("");
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
      ) : (
        <button
          className="flex items-center gap-1 mt-1 px-1 text-sm text-slate-500 hover:text-slate-700"
          onClick={() => setIsAddingItem(true)}
        >
          <AddIcon style={{ fontSize: 16 }} />
          Add an item
        </button>
      )}
    </div>
  );
}

const BoardCardChecklists = ({ card, storeId, onCardUpdated }) => {
  const [isAddingChecklist, setIsAddingChecklist] = useState(false);
  const [newChecklistName, setNewChecklistName] = useState("");
  const checklists = card.checklists || [];

  const apiCall = async (url, body) => {
    try {
      const res = await postRightworkAPI(url, {
        store_id: storeId,
        board_card_uuid: card.uuid,
        ...body,
      });
      const updated = res.data?.redux?.payload?.board_card;
      if (updated) onCardUpdated(updated);
    } catch (error) {
      toast.error("Failed to update checklist");
    }
  };

  const addChecklist = () => {
    const name = newChecklistName.trim();
    if (!name) return;
    apiCall(API_create_board_checklist, { name });
    setNewChecklistName("");
    setIsAddingChecklist(false);
  };

  const renameChecklist = (checklistUuid, name) =>
    apiCall(API_update_board_checklist, { board_checklist_uuid: checklistUuid, name });

  const deleteChecklist = (checklistUuid) =>
    apiCall(API_delete_board_checklist, { board_checklist_uuid: checklistUuid });

  const addItem = (checklistUuid, text) =>
    apiCall(API_create_board_checklist_item, {
      board_checklist_uuid: checklistUuid,
      text,
    });

  const deleteItem = (itemUuid) =>
    apiCall(API_delete_board_checklist_item, {
      board_checklist_item_uuid: itemUuid,
    });

  // Toggling an item is the frequent action — update optimistically.
  const toggleItem = (checklist, item) => {
    const optimistic = {
      ...card,
      checklists: card.checklists.map((cl) => {
        if (cl.uuid !== checklist.uuid) return cl;
        const newItems = cl.items.map((it) =>
          it.uuid === item.uuid ? { ...it, is_complete: !it.is_complete } : it
        );
        return {
          ...cl,
          items: newItems,
          completed_count: newItems.filter((it) => it.is_complete).length,
        };
      }),
    };
    onCardUpdated(optimistic);
    postRightworkAPI(API_update_board_checklist_item, {
      store_id: storeId,
      board_card_uuid: card.uuid,
      board_checklist_item_uuid: item.uuid,
      is_complete: !item.is_complete,
    })
      .then((res) => {
        const updated = res.data?.redux?.payload?.board_card;
        if (updated) onCardUpdated(updated);
      })
      .catch(() => {
        toast.error("Failed to update item");
        onCardUpdated(card);
      });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-slate-500">CHECKLISTS</p>
        {!isAddingChecklist && (
          <button
            className="flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-800"
            onClick={() => setIsAddingChecklist(true)}
          >
            <AddIcon style={{ fontSize: 15 }} />
            Add
          </button>
        )}
      </div>

      {isAddingChecklist && (
        <div className="mb-3">
          <TextField
            autoFocus
            value={newChecklistName}
            onChange={(e) => setNewChecklistName(e.target.value)}
            placeholder="Checklist name"
            fullWidth
            variant="outlined"
            size="small"
            inputProps={{ maxLength: 255 }}
            sx={textFieldSx}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addChecklist();
              } else if (e.key === "Escape") {
                setIsAddingChecklist(false);
                setNewChecklistName("");
              }
            }}
          />
          <div className="flex items-center gap-2 mt-1.5">
            <button
              className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
              onClick={addChecklist}
            >
              Add checklist
            </button>
            <button
              className="text-slate-500 hover:text-slate-700"
              onClick={() => {
                setIsAddingChecklist(false);
                setNewChecklistName("");
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {checklists.length === 0 && !isAddingChecklist && (
        <p className="text-xs text-slate-400">No checklists.</p>
      )}

      {checklists.map((cl) => (
        <ChecklistBlock
          key={cl.uuid}
          checklist={cl}
          onToggleItem={toggleItem}
          onAddItem={addItem}
          onDeleteItem={deleteItem}
          onRename={renameChecklist}
          onDelete={deleteChecklist}
        />
      ))}
    </div>
  );
};

export default BoardCardChecklists;
